import { useDispatch, useSelector } from "../../services/hooks";
import { keyPressed } from "../../services/trainer/actions";
import { getCursorIndex, getTrainString, getTrainStringCharStatus, getTypingSpeed, getAccuracy, getErrorRate } from "../../services/trainer/selectors";
import { useEffect, useRef } from "react";
import styles from "./trainer.module.css";
import { WS_CONNECTION_START, wsSendMessage } from "../../services/middleware/actions";
import { WS_BASE_URL } from "../../config/api";

export function Trainer(): JSX.Element {
  const dispatch = useDispatch();
  const trainString = useSelector(getTrainString);
  const cursorIndex = useSelector(getCursorIndex);
  const typingSpeed = useSelector(getTypingSpeed);
  const accuracy = useSelector(getAccuracy);
  const errorRate = useSelector(getErrorRate);

  const trainStringCharStatus = useSelector(getTrainStringCharStatus);
  const cursorIndexRef = useRef(cursorIndex);

  const user = useSelector((store) => store.loginReducer.user);

  useEffect(() => {
    cursorIndexRef.current = cursorIndex;
  }, [cursorIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      dispatch(keyPressed(event.key));
      dispatch(wsSendMessage(event.key, cursorIndexRef.current));
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    let url: string;

    if (!token) {
      url = `${WS_BASE_URL}/ws/demo`;
    } else {
      url = `${WS_BASE_URL}/ws`;
    }
    
    dispatch({
      type: WS_CONNECTION_START,
      payload: url,
    });
  }, [dispatch]);

  return (
    <div className={styles.page}>
      <section className={styles.section} aria-label="trainer">
        <div className={styles.subtitle}>Practice speed and accuracy</div>

        <div className={styles.trainerText}>
          {trainString.split("").map((char, index) => {
            const status = trainStringCharStatus[index];
            let charType = styles.trainerTextUnfinished;

            if (status === 1) {
              charType = styles.trainerTextCorrect;
            }
            if (status === 0) {
              charType = styles.trainerTextUnfinished;
            }
            if (status === -1) {
              charType = styles.trainerTextError;
            }
            if (index === cursorIndex) {
              charType = styles.trainerCursorChar;
            }

            return (
              <span key={`${char}-${index}`} className={charType}>
                {char}
              </span>
            );
          })}
        </div>

        <div className={styles.statistics} aria-label="statistics">
          {user ?
            <>
              <div className={styles.stat}>
                <b className={styles.statValue}>Typing Speed: {typingSpeed?.toFixed(2) ?? "0.00"}</b>
                <span className={styles.statLabel}>CPM</span>
              </div>

              <div className={styles.stat}>
                <b className={styles.statValue}>Accuracy: {accuracy?.toFixed(2) ?? "0.00"}</b>
                <span className={styles.statLabel}>% ACC</span>
              </div>

              <div className={styles.stat}>
                <b className={styles.statValue}>Error Rate: {errorRate?.toFixed(2) ?? "0.00"}</b>
                <span className={styles.statLabel}>% ERR</span>
              </div>
            </>
            : <div className={styles.stat}>Login to see your statistics</div>
          }
        </div>
      </section>
    </div>
  );
}
